import React from 'react';
import { AlertTriangle, ArrowDownToLine, Info } from 'lucide-react';

// Square stock sync summary for one trade order: the warning banner (lines
// deducted below zero, bypassed made-to-order lines, failed pushes), the per-line
// outcome of the deduction, any restock that followed a cancellation, and the
// stock audit trail.

const STATUS_STYLE = {
  deducted: 'text-cyan-400 border-cyan-400/30',
  restocked: 'text-green-400 border-green-400/30',
  skipped: 'text-white/40 border-white/15',
  failed: 'text-red-400 border-red-400/30',
  pending: 'text-white/40 border-white/15',
};

const STATUS_LABEL = {
  deducted: 'DEDUCTED',
  restocked: 'RESTOCKED',
  skipped: 'BYPASSED',
  failed: 'FAILED',
  pending: 'PENDING',
};

function LineRow({ result }) {
  const level = result.square_level_after != null ? ` · Square level ${result.square_level_after}` : '';
  return (
    <div className="flex items-start gap-3 py-1.5 border-b border-white/5 last:border-0">
      <span className={`shrink-0 text-[9px] font-bold tracking-widest border px-1.5 py-0.5 ${STATUS_STYLE[result.status] || STATUS_STYLE.pending}`}>
        {STATUS_LABEL[result.status] || result.status}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-white/70 text-xs">
          <span className="font-mono">{result.product_code || '—'}</span> · {result.qty} unit{result.qty === 1 ? '' : 's'}
          {level && <span className={result.below_zero ? ' text-red-400' : ' text-white/40'}>{level}</span>}
        </p>
        {result.reason && <p className="text-white/30 text-[11px] mt-0.5">{result.reason}</p>}
      </div>
    </div>
  );
}

export default function SquareStockPanel({ order }) {
  const deduction = order.square_stock_deduction || [];
  const restock = order.square_stock_restock || [];
  const audit = order.stock_audit || [];
  if (deduction.length === 0 && restock.length === 0 && audit.length === 0) return null;

  const belowZero = deduction.filter((r) => r.below_zero);
  const failed = deduction.filter((r) => r.status === 'failed');
  const skipped = deduction.filter((r) => r.status === 'skipped');

  return (
    <div className="space-y-3">
      {belowZero.length > 0 && (
        <div className="attention-red border bg-red-500/[0.04] p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <p className="text-red-400 text-[10px] font-bold tracking-widest">DEDUCTED BELOW ZERO IN SQUARE</p>
          </div>
          <ul className="space-y-1">
            {belowZero.map((r, i) => (
              <li key={i} className="text-white/70 text-xs">
                Item {r.product_code || r.description} deducted below zero in Square — now {r.square_level_after}
              </li>
            ))}
          </ul>
        </div>
      )}

      {failed.length > 0 && (
        <div className="attention-amber border bg-amber-500/[0.04] p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <p className="text-amber-400 text-[10px] font-bold tracking-widest">SQUARE STOCK PUSH FAILED</p>
          </div>
          <ul className="space-y-1">
            {failed.map((r, i) => (
              <li key={i} className="text-white/70 text-xs">
                Item {r.product_code || r.description} — {r.reason || 'Square rejected the adjustment'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {skipped.length > 0 && (
        <div className="border border-amber-400/25 bg-amber-400/[0.02] p-4">
          <div className="flex items-center gap-2 mb-2">
            <Info className="w-4 h-4 text-amber-400" />
            <p className="text-amber-400 text-[10px] font-bold tracking-widest">SQUARE SYNC BYPASSED</p>
          </div>
          <ul className="space-y-1">
            {skipped.map((r, i) => (
              <li key={i} className="text-white/60 text-xs">
                Item {r.product_code || r.description} — {r.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="border border-white/10 bg-[#0d0d0f] p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <p className="text-white/40 text-[10px] font-bold tracking-widest flex items-center gap-2">
            <ArrowDownToLine className="w-3.5 h-3.5" /> SQUARE STOCK SYNC
          </p>
          <div className="flex items-center gap-2">
            {order.square_stock_deducted && (
              <span className="text-cyan-400 text-[10px] font-bold tracking-widest">DEDUCTED AT INVOICE</span>
            )}
            {order.square_stock_restocked && (
              <span className="text-green-400 text-[10px] font-bold tracking-widest">RETURNED TO STOCK</span>
            )}
          </div>
        </div>

        {deduction.length > 0 ? (
          <div>{deduction.map((r, i) => <LineRow key={i} result={r} />)}</div>
        ) : (
          <p className="text-white/30 text-xs">No Square adjustment has been sent for this order yet.</p>
        )}

        {restock.length > 0 && (
          <div className="mt-4 pt-3 border-t border-white/10">
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-2">RESTOCK ON CANCELLATION</p>
            <div>{restock.map((r, i) => <LineRow key={i} result={r} />)}</div>
          </div>
        )}

        {audit.length > 0 && (
          <div className="mt-4 pt-3 border-t border-white/10 space-y-1">
            {audit.map((a, i) => (
              <p key={i} className="text-white/30 text-[11px]">
                {a.at ? new Date(a.at).toLocaleString('en-GB') : ''} · {a.admin_name || a.admin_email || 'system'} — {a.summary}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}